/**
 * 运营分析页面状态管理模块
 *
 * @author By AICode
 * @date 2026-04-02
 */

import axiosCommon from "@/api/axiosCommon";
import moment from "moment";

const operation = {
  namespaced: true,
  state: {
    metadata: { dimensions: [], metrics: [] }, // 存储元数据
    sheetList: [], // 存储所有的Sheet列表
    currentSheet: null, // 当前选中的Sheet配置
    analysisData: [], // 运营分析表格数据
    analysisTotal: 0, // 分析数据总数
    dimensionOptions: {}, // 维度检索选项缓存 (key -> options array)
    isClickTypeFlag: false, // 是否点击了类型，再进行检索
  },
  mutations: {
    // 更新列元数据 - By AICode
    setMetadata(state, payload) {
      state.metadata.dimensions = payload?.dimensions || [];
      state.metadata.metrics = payload?.metrics || [];
    },
    // 更新Sheet列表 - By AICode
    setSheetList(state, payload) {
      state.sheetList = payload || [];
    },
    // 更新当前Sheet配置 - By AICode
    setCurrentSheet(state, payload) {
      state.currentSheet = payload;
    },
    // 更新分析数据 - By AICode
    setAnalysisData(state, payload) {
      state.analysisData = payload || [];
    },
    // 更新分析数据总数 - By AICode
    setAnalysisTotal(state, payload) {
      state.analysisTotal = payload || 0;
    },
    // 缓存维度检索选项 - By AICode
    setDimensionOptions(state, { field, options }) {
      state.dimensionOptions = {
        ...state.dimensionOptions,
        [field]: options || [],
      };
    },
    alreadyClickType(state, payload) {
      state.isClickTypeFlag = payload;
    },
  },
  actions: {
    // 获取当前账号下全部Sheet列表 - By AICode
    async getSheetList({ commit }) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/list",
          {},
        );
        if (result) {
          commit("setSheetList", result);
          return result;
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 获取单个Sheet配置 - By AICode
    async getSheet({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/get",
          params,
        );
        if (result) {
          commit("setCurrentSheet", result);
          return result;
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 保存Sheet（覆盖已有配置） - By AICode
    async saveSheet({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/save",
          params,
        );
        return result;
      } catch (error) {
        console.error(error);
      }
    },

    // 另存为新Sheet - By AICode
    async saveAsSheet({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/saveAs",
          params,
        );
        return result;
      } catch (error) {
        console.error(error);
      }
    },

 

    // Sheet批量删除 - By AICode
    async batchDeleteSheets({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/batchDelete",
          params,
        );
        return result;
      } catch (error) {
        console.error(error);
      }
    },

    // Sheet配置同步（排序/重命名/删除统一提交） - By AICode
    async syncSheets({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/sheet/sync",
          params,
        );
        return result;
      } catch (error) {
        console.error(error);
      }
    },

    // 分析结果查询（分页） - By AICode
    async queryAnalysisData({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/query",
          params,
        );
        if (result) {
          commit("setAnalysisData", result.results || []);
          commit("setAnalysisTotal", result.totalRecord || 0);
          return result;
        }
      } catch (error) {
        console.error(error);
        commit("setAnalysisData", []);
        commit("setAnalysisTotal", 0);
      }
    },

    // 获取列元数据信息用于动态构建展示列，避免前端硬编码字段映射 - By AICode
    async getColumnMetadata({ commit }) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/columnMetadata",
        );
        if (result) {
          const metadataPayload = result;
          commit("setMetadata", metadataPayload || {});
          return result;
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 串联获取 Sheet 配置并按配置查询分析数据，减少页面重复拼装逻辑 - By AICode
    async loadSheetConfigAndAnalysis({ dispatch }, params) {
      const queryParams = params || {};
      const sheetId = queryParams.sheetId || queryParams.id;
      if (!sheetId) {
        return null;
      }
      const sheetConfig = await dispatch("getSheet", { sheetId, id: sheetId });
      if (!sheetConfig) {
        return null;
      }
      const fallbackColumns = queryParams.fallbackColumns || [];
      // 直接使用接口返回的 columns 字段，为空时才 fallback 到默认全量列 - By AICode
      const columns =
        Array.isArray(sheetConfig.columns) && sheetConfig.columns.length
          ? [...sheetConfig.columns]
          : [...fallbackColumns];
      const defaultSort = columns[0]
        ? [{ columnKey: columns[0], order: "asc" }]
        : [];
      let sort =
        Array.isArray(sheetConfig.sort) && sheetConfig.sort.length
          ? sheetConfig.sort
          : defaultSort;
      const requestBody = {
        columns,
        applyTimeFrom: sheetConfig.applyTimeFrom || "",
        applyTimeTo: sheetConfig.applyTimeTo || "",
        activeTimeFrom:
          sheetConfig.activeTimeFrom ||
          moment()
            .subtract(30, "days")
            .format("YYYY-MM-DD HH:mm"),
        activeTimeTo:
          sheetConfig.activeTimeTo || moment().format("YYYY-MM-DD HH:mm"),
        filters: sheetConfig.filters || {},
        sort,
        pageNo: queryParams.pageNo || 1,
        pageSize: queryParams.pageSize || 10,
      };
      const analysis = await dispatch("queryAnalysisData", requestBody);
      return {
        sheetConfig,
        columns,
        sort,
        analysis,
      };
    },

    // 运营分析维度检索 - By AICode
    async searchDimension({ commit }, params) {
      try {
        const result = await axiosCommon.post(
          "/rest/mgmt/analysis/search",
          params,
        );
        if (result && result.results) {
          commit("setDimensionOptions", {
            field: params.field,
            options: result.results,
          });
          return result;
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 根据多功能检索项，展示搜索框检索表项
    async queryDataInType({ commit }, params) {
      let result = await axiosCommon.post("/rest/mgmt/analysis/search", params);
      if (result) {
        return result;
      }
    },
  },
};

export default operation;
